import type { FC } from "react";

import { PlainTooltip, PixelPlaceholderLoader } from "@similarweb/ui-components";

import { chosenSites } from "app/common/services/chosenSites";
import { i18nFilter } from "app/filters/i18nFilter";
import {
    SerpIconComponent,
    SerpIconsStacked,
    SerpIconsGrouped,
} from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpStyledComponents";
import { SerpTableCellTooltipSingle } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellTooltipSingle";
import { buildGroupedSerpFeatures } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTableCellUtils";
import { SERP_MAP } from "app/pages/website-analysis/traffic-sources/search/components/serp/SerpTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { SerpTableCellTooltipCompare } from "./SerpTableCellTooltipCompare";

type SERP_MAP_TYPE = typeof SERP_MAP;

interface ISerpTableCellCompareProps {
    row;
    tableOptions?: {
        onSerpIconClick?: (value: SERP_MAP_TYPE[string]) => void;
        sitesForSerpColumn?: any[];
        presentLastUpdatedSubtitleSerpColumn?: boolean;
        serpFeaturesMap?: SERP_MAP_TYPE;
    };
}

export const SerpTableCellCompare: FC<ISerpTableCellCompareProps> = ({
    row,
    tableOptions: {
        onSerpIconClick,
        sitesForSerpColumn = chosenSites.sitelistForLegend(),
        presentLastUpdatedSubtitleSerpColumn = true,
        serpFeaturesMap = SERP_MAP,
    },
}) => {
    if (row.serpFailed) {
        return (
            <PlainTooltip
                placement="top"
                tooltipContent={i18nFilter()("serp.error.tooltip")}
            >
                <div>N/A</div>
            </PlainTooltip>
        );
    }
    if (row.serpLoading) {
        return (
            <PixelPlaceholderLoader
                width={79}
                height={16}
            />
        );
    }
    const { SearchTerm, SerpFeatures = [], SerpScrapeDate, SiteSerpFeatures = {} } = row;
    const serpFeatures = buildGroupedSerpFeatures(
        SerpFeatures,
        SiteSerpFeatures,
        sitesForSerpColumn,
        SerpScrapeDate,
        SearchTerm,
        presentLastUpdatedSubtitleSerpColumn,
        serpFeaturesMap,
    );
    const hasFeaturedSerp = Object.keys(serpFeatures.featured).length > 0;
    const hasNonFeaturedSerp = serpFeatures.nonFeatured.length > 0;
    if (!hasFeaturedSerp && !hasNonFeaturedSerp) {
        return <span>-</span>;
    }
    // in this case, When there are no site that has this serp, we want to show the tooltip like in "single" mode
    const TooltipComponent = hasFeaturedSerp ? SerpTableCellTooltipCompare : SerpTableCellTooltipSingle;
    const featured = Object.entries(serpFeatures.featured).slice(0, 5);
    const nonFeatured = serpFeatures.nonFeatured.slice(0, Math.min(5, 5 - featured.length));
    const restFeatures =
        Object.entries(serpFeatures.featured).length +
        serpFeatures.nonFeatured.length -
        featured.length -
        nonFeatured.length;
    return (
        <TooltipComponent
            serpFeatures={serpFeatures}
            serpFeaturesMap={serpFeaturesMap}
        >
            <FlexRow alignItems="center">
                {featured.slice(0, Math.min(featured.length, 5)).map(([serp, sites], index) => {
                    return (
                        <SerpIconsStacked key={`group-${index}`}>
                            {sites.map(({ color }, index) => {
                                return (
                                    <SerpIconComponent
                                        key={`featured-${index}`}
                                        background={color}
                                        iconName={serpFeaturesMap[serp].icon}
                                        onClick={() => onSerpIconClick(serpFeaturesMap[serp])}
                                    />
                                );
                            })}
                        </SerpIconsStacked>
                    );
                })}
                <SerpIconsGrouped>
                    {nonFeatured.map((serp, index) => {
                        return (
                            <SerpIconComponent
                                key={`non-featured-${index}`}
                                iconName={serpFeaturesMap[serp].icon}
                                onClick={() => onSerpIconClick(serpFeaturesMap[serp])}
                            />
                        );
                    })}
                </SerpIconsGrouped>
                {restFeatures > 0 ? `+${restFeatures}` : null}
            </FlexRow>
        </TooltipComponent>
    );
};
